import React, { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { getTestId } from '../hooks/useTestId';
import { t } from '@lingui/core/macro';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import FileApi from '../api/FileApi';
import { DataRemarkMessage } from '../api/DataRemarksApi';
import { WFAccessToken } from '../api/types';
import { getAvatarColor } from '../util/avatarColor';
import { useDarkMode } from '../context/DarkModeContext';
import sortBy from 'lodash/sortBy';
import Icon from 'src/components/Icon';

dayjs.extend(utc);
dayjs.extend(localizedFormat);

export function groupMessages(messages: DataRemarkMessage[]): DataRemarkMessage[][] {
  const groups: DataRemarkMessage[][] = [];
  for (const msg of messages) {
    const lastGroup = groups[groups.length - 1];
    const userId = msg.createdBy?.id ?? null;
    const lastUserId = lastGroup ? lastGroup[0].createdBy?.id ?? null : undefined;
    if (lastGroup && lastUserId === userId) {
      lastGroup.push(msg);
    } else {
      groups.push([msg]);
    }
  }
  return groups;
}

function formatDate(date: string, locale: string = 'es') {
  return dayjs
    .utc(date)
    .local()
    .locale(locale)
    .format('lll');
}

type AttachmentRowProps = {
  message: DataRemarkMessage;
  wfAccessToken?: WFAccessToken;
};

function AttachmentRow({ message, wfAccessToken }: AttachmentRowProps) {
  const [loading, setLoading] = useState(false);
  const { isDarkMode } = useDarkMode();

  const getUrl = async (): Promise<string | null> => {
    setLoading(true);
    try {
      const file = await FileApi.getFile({ fileId: message.attachmentId!, wfAccessToken });
      return file?.url ?? file?.URL ?? null;
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    const url = await getUrl();
    if (url) window.open(url, '_blank');
  };

  const handlePreview = async () => {
    const url = await getUrl();
    if (url) window.open(`${url}?inline=true`, '_blank');
  };

  const name = message.attachmentName ?? 'Attachment';
  const iconStyle: React.CSSProperties = { fontSize: '0.85rem', opacity: loading ? 0.4 : 1 };

  return (
    <div
      className="flex items-center gap-2 mt-2 px-2 py-1"
      style={{ border: `1px solid ${isDarkMode ? '#40444f' : '#dee2e6'}`, borderRadius: 4, display: 'inline-flex', maxWidth: '100%' }}
    >
      <Icon icon="Paperclip" className="text-muted flex-shrink-0" style={{ fontSize: '0.8rem' }} />
      <span className="small text-muted flex-grow" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {name}
      </span>
      <div className="flex gap-2 flex-shrink-0">
        <button className="btn btn-link p-0 border-0" style={iconStyle} onClick={handleDownload} disabled={loading} title={t`Download`} {...getTestId('message-attachment', '.', 'download', '-', message.id)}>
          <Icon icon="Download" />
        </button>
        <button className="btn btn-link p-0 border-0" style={iconStyle} onClick={handlePreview} disabled={loading} title={t`Preview`} {...getTestId('message-attachment', '.', 'preview', '-', message.id)}>
          <Icon icon="Eye" />
        </button>
      </div>
    </div>
  );
}

export type MessageBubbleProps = {
  messages: DataRemarkMessage[];
  currentUserId?: number | null;
  wfAccessToken?: WFAccessToken;
};

function MessageBubble({ messages, currentUserId, wfAccessToken }: MessageBubbleProps) {
  const {
    i18n: { locale },
  } = useLingui();

  const firstMsg = messages[0];
  const accentColor = getAvatarColor(firstMsg.createdBy?.name ?? '');

  const containerStyle: React.CSSProperties = {
    borderLeft: `3px solid ${accentColor}`,
    backgroundColor: `${accentColor}12`,
    borderRadius: '0.25rem',
  };

  const sortedMessages = sortBy(messages, [m => m.createdAt]);

  return (
    <div className={`mb-3 flex flex-col items-start`}>
      {/* Group header: name only */}
      <div className={`flex items-baseline gap-1 mb-1`}>
        <span className="font-bold small">{firstMsg.createdBy?.name ?? <Trans>Unknown</Trans>}</span>
        {firstMsg?.createdBy?.organizationalUnit != null && (
          <small>
            - <em>{firstMsg?.createdBy?.organizationalUnit?.name}</em>
          </small>
        )}
      </div>

      {/* Message container */}
      <div style={{ ...containerStyle, padding: '8px 12px', maxWidth: '85%', minWidth: 0 }}>
        {sortedMessages.map((msg, i) => (
          <div key={msg.id} className={i < messages.length - 1 ? 'mb-3' : ''}>
            <div className={`flex items-end gap-2`}>
              <p className="mb-0" style={{ fontSize: '0.875rem', wordBreak: 'break-word', minWidth: 0 }}>
                {msg.message}
              </p>
              <span className="text-muted flex-shrink-0" style={{ fontSize: '0.68rem' }}>
                {formatDate(msg.createdAt, locale)}
              </span>
            </div>
            {msg.attachmentId != null && <AttachmentRow message={msg} wfAccessToken={wfAccessToken} />}
          </div>
        ))}
      </div>
    </div>
  );
}

export default MessageBubble;
